import { useState } from 'react'
import CapabilityList from './CapabilityList'
import CopyField from './CopyField'
import SettingsForm from './SettingsForm'
import SyncChip from './SyncChip'
import { errorMessage } from '../lib/api'
import { formatDateTime, formatStay } from '../lib/format'
import { useExternalUnits, useIntegrationSettings, useRemoveIntegration, useSyncIntegration } from '../lib/hooks'
import { externalTypes } from '../lib/mappings'
import type { Integration } from '../lib/types'

interface Props {
  slug: string
  integration: Integration
  unitCount: number
}

export default function IntegrationCard({ slug, integration, unitCount }: Props) {
  const [asked, setAsked] = useState(false)
  const [days, setDays] = useState('120')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const settings = useIntegrationSettings(slug)
  const toggle = useIntegrationSettings(slug)
  const remove = useRemoveIntegration(slug)
  const sync = useSyncIntegration(slug)
  const external = useExternalUnits(integration.id, asked)

  const cannotList = external.error?.status === 422
  // Absolute and on the API host: the admin host does not serve /webhooks.
  const webhookUrl = integration.webhookUrl
  const stay = sync.data ? formatStay(sync.data.from, sync.data.to) : null

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>{integration.label}</h2>
          <p className="muted small">
            {integration.connector} · {integration.mappedUnits} van {unitCount} plaatsen gekoppeld · laatste synchronisatie{' '}
            {formatDateTime(integration.lastSyncedAt)}
          </p>
        </div>
        <div className="spacer" />
        <SyncChip integration={integration} />
      </div>

      {integration.lastSyncError && (
        <p className="inline-error" role="alert">
          Laatste fout: {integration.lastSyncError}
        </p>
      )}

      <CapabilityList capabilities={integration.capabilities} />

      <div className="actions" style={{ marginTop: 0 }}>
        <label className="check">
          <input
            type="checkbox"
            checked={integration.enabled}
            disabled={toggle.isPending}
            onChange={(event) => toggle.mutate({ id: integration.id, enabled: event.target.checked })}
          />
          Koppeling ingeschakeld
        </label>
        {toggle.isError && <span className="inline-error">{errorMessage(toggle.error)}</span>}
        <div className="spacer" style={{ flex: 1 }} />
        {confirmDelete ? (
          <>
            <span className="small">Koppeling en alle mappings verwijderen?</span>
            <button
              type="button"
              className="btn danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate(integration.id)}
            >
              Ja, verwijderen
            </button>
            <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
              Annuleren
            </button>
          </>
        ) : (
          <button type="button" className="btn danger" onClick={() => setConfirmDelete(true)}>
            Verwijderen
          </button>
        )}
      </div>
      {remove.isError && <p className="inline-error">{errorMessage(remove.error)}</p>}

      <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '1rem 0' }} />

      <h3>Instellingen</h3>
      <SettingsForm
        integration={integration}
        pending={settings.isPending}
        error={settings.isError ? errorMessage(settings.error) : null}
        saved={settings.isSuccess}
        onSave={(values, clearSettings) => settings.mutate({ id: integration.id, settings: values, clearSettings })}
      />

      {webhookUrl && (
        <div style={{ marginTop: '1rem', maxWidth: '38rem' }}>
          <CopyField
            id={`webhook-${integration.id}`}
            label="Webhook-URL"
            value={webhookUrl}
            help="Zet deze URL in het boekingssysteem, dan komen wijzigingen meteen binnen."
          />
        </div>
      )}

      <h3 style={{ marginTop: '1.2rem' }}>Controleren</h3>
      <div className="actions" style={{ marginTop: 0 }}>
        <button
          type="button"
          className="btn"
          disabled={external.isFetching}
          onClick={() => (asked ? void external.refetch() : setAsked(true))}
        >
          {external.isFetching ? 'Bezig…' : 'Types ophalen'}
        </button>
        {external.isFetching && <span className="spinner" aria-hidden="true" />}
        {!external.isFetching && cannotList && <span className="muted small">{errorMessage(external.error)}</span>}
        {!external.isFetching && !cannotList && external.isError && (
          <span className="inline-error">{errorMessage(external.error)}</span>
        )}
        {!external.isFetching && external.data && (
          <span className="inline-ok">
            {external.data.units.length} accommodaties, {externalTypes(external.data.units).length} types
          </span>
        )}
      </div>

      <div className="actions" style={{ marginTop: '0.5rem' }}>
        <label className="check">
          <span className="visually-hidden">Aantal dagen</span>
          <input
            type="text"
            inputMode="numeric"
            className="num"
            style={{ width: '4rem' }}
            value={days}
            disabled={integration.capabilities.onDemandStay}
            onChange={(event) => setDays(event.target.value)}
          />
          dagen
        </label>
        <button
          type="button"
          className="btn"
          disabled={sync.isPending}
          onClick={() => {
            const parsed = Number.parseInt(days, 10)
            sync.mutate({ id: integration.id, days: Number.isFinite(parsed) ? parsed : undefined })
          }}
        >
          {sync.isPending ? 'Synchroniseren…' : 'Nu synchroniseren'}
        </button>
        {sync.isPending && <span className="spinner" aria-hidden="true" />}
        {sync.isError && <span className="inline-error">{errorMessage(sync.error)}</span>}
        {sync.data && !sync.isPending && (
          <span className={sync.data.written === 0 ? 'muted small' : 'inline-ok'}>
            {sync.data.onDemand
              ? `Verbinding werkt, ${sync.data.written} plaatsnachten${stay ? ` voor ${stay}` : ''}`
              : sync.data.written === 0
                ? 'Verbinding werkt, maar het boekingssysteem leverde geen nachten.'
                : `${sync.data.written} plaatsnachten bijgewerkt`}
          </span>
        )}
        {integration.capabilities.onDemandStay && (
          <span className="muted small">
            Dit systeem beantwoordt één verblijf per aanvraag, dus wordt het komende weekend gecontroleerd.
          </span>
        )}
      </div>
    </section>
  )
}
